import { useNodeConnections, useReactFlow } from '@xyflow/react';
import { memo, useEffect } from 'react';
import { ImagePrimitive } from './primitive';
import { ImageTransform } from './transform';
import { useI18n } from '@/providers/i18n';
import { areNodePropsEqual } from '../are-node-props-equal';
import { useWhyNodeRender, usePerfSentinel } from '@/lib/debug/use-perf';
import { getIsCanvasPositionInteractingSync } from '@/components/canvas/utils/drag-state-store';

export type ImageNodeProps = {
  type: string;
  data: {
    content?: {
      url: string;
      type: string;
    };
    /** 原始导入的图片 URL（通过工具栏输入的 URL） */
    sourceUrl?: string;
    generated?: {
      url?: string;
      type?: string;
      /** 多图输出（例如 imagesCount > 1） */
      urls?: string[];
      /** 生成结果的真实像素尺寸（"WxH"，唯一真实尺寸来源；用于 UI 自动按图像比例调整节点） */
      size?: string;
      /** size 对应的图片 URL（用于在切换主图时判断是否需要重新测量） */
      sizeUrl?: string;
    };
    size?: string;
    width?: number;
    height?: number;
    updatedAt?: string;
    model?: string;
    description?: string;
    instructions?: string;
    /**
     * 本节点额外 prompt（非输入框编辑）：用于插件/系统自动写入，
     * 会参与最终提交给生成接口的 prompt 拼接。
     */
    nodePrompt?: string;
    /**
     * 摄像机控制插件的选择状态（用于回显）。
     * 注意：此字段仅用于 UI/插件，不直接影响生成；生成依赖 nodePrompt。
     */
    cameraControl?: {
      cameraId: string;
      lensId: string;
      focalId: string;
      apertureId: string;
    };
    /** 节点视图模式：transform 或 primitive */
    viewMode?: 'transform' | 'primitive';
  };
  id: string;
};

export const ImageNode = memo((props: ImageNodeProps) => {
  const __why = useWhyNodeRender('ImageNode', props);
  // 哨兵：拖拽期间节点的 data 不应变化（位置走 ReactFlow 内部 store）。
  // 若此处触发，说明拖拽连带改了节点 data，会拖累大画布拖拽帧率。
  usePerfSentinel(
    'imageNodeRenderDuringDrag',
    getIsCanvasPositionInteractingSync(),
    __why,
  );
  const { t } = useI18n();
  const { getNode, updateNodeData } = useReactFlow();
  const connections = useNodeConnections({ id: props.id, handleType: 'target' });
  const hasNonFakeIncoming = connections.some((c) => {
    const n = getNode(c.source);
    return n && n.type !== 'video' && n.type !== 'magic';
  });
  const existingViewMode = (props.data as any)?.viewMode;
  
  // 一旦节点变成 transform，就永久保持 transform，不会变回 primitive
  const isTransform = existingViewMode === 'transform' || hasNonFakeIncoming;
  const viewMode = isTransform ? 'transform' : 'primitive';
  const Component = isTransform ? ImageTransform : ImagePrimitive;

  // 当节点首次变成 transform 时，保存 viewMode；若已有 content 且尚无 generated，则将 content 复制到 generated
  // Perf (Phase 9.4): depend on primitive fields instead of `props.data` whole
  // object — otherwise this effect re-runs on EVERY data field change
  // (history, instructions, etc.) even though only content/generated/viewMode
  // matter here.
  const dataContentUrl = (props.data as any)?.content?.url;
  const dataContentType = (props.data as any)?.content?.type;
  const dataGeneratedUrl = (props.data as any)?.generated?.url;
  useEffect(() => {
    if (!isTransform) return;
    const hasGenerated = Boolean(dataGeneratedUrl);
    if (existingViewMode !== 'transform') {
      const updates: any = { viewMode: 'transform' };
      if (dataContentUrl && !hasGenerated) {
        updates.generated = { url: dataContentUrl, type: dataContentType };
      }
      updateNodeData(props.id, updates);
    } else if (dataContentUrl && !hasGenerated) {
      updateNodeData(props.id, {
        generated: { url: dataContentUrl, type: dataContentType },
      });
    }
  }, [
    props.id,
    isTransform,
    existingViewMode,
    dataContentUrl,
    dataContentType,
    dataGeneratedUrl,
    updateNodeData,
  ]);

  return <Component {...props} title={t('Image Node')} />;
}, areNodePropsEqual);
ImageNode.displayName = 'ImageNode';
